import { FC, Fragment } from 'react';
import { Avatar } from 'antd';
import type { AvatarSize } from 'antd/es/avatar/AvatarContext';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ActiveAvatarHoverDropdown } from '@/shared/components/dropdowns';
import type { CompactActiveAvatarCollaborator } from '@/shared/components/groups/CompactActiveAvatarGroup';
import { getPresenceAvatarStyle } from '@/shared/components/groups/CompactActiveAvatarGroup/presence';
import { ActivityPayload, useActivityStore, usePresenceStore } from '@/stores';

interface ActiveAvatarItemProps {
    collaborator: CompactActiveAvatarCollaborator;
    resourceId?: string;
    size?: AvatarSize;
    tooltipPlacement?:
        | 'bottom'
        | 'bottomCenter'
        | 'bottomLeft'
        | 'bottomRight'
        | 'top'
        | 'topCenter'
        | 'topLeft'
        | 'topRight';
    type?: 'investment' | 'team';
}

const ActiveAvatarItem: FC<ActiveAvatarItemProps> = ({
    collaborator,
    resourceId,
    size,
    tooltipPlacement,
    type,
}): JSX.Element => {
    const activities = useActivityStore((s) => s.users);
    const user = collaborator?.member?.user ?? collaborator?.user;

    function getActivityForUser(users: Record<string, ActivityPayload>, userId?: string, resourceId?: string) {
        if (!userId) {
            return undefined;
        }

        const activity = users[userId];

        if (resourceId && activity?.resourceId !== resourceId) {
            return undefined;
        }

        return activity;
    }

    const activity = getActivityForUser(activities, user?.id, resourceId);

    const presences = usePresenceStore((s) => s.users);
    const presence = user?.id ? presences[user.id] : undefined;
    const presenceStyle = getPresenceAvatarStyle(presence?.status);
    let avatarOpacity = presenceStyle.opacity;
    let scale = 0.95;

    if (activity) {
        if (activity.type === 'resource:edit') {
            avatarOpacity = 1.0;
            scale = 1.0;
        } else if (activity.type === 'resource:view') {
            avatarOpacity = 1.0;
            scale = 1.0;
        } else if (activity.type === 'idle') {
            avatarOpacity = 0.3;
            scale = 0.9;
        }
    }

    const { token } = useAntDesignTheme();

    return (
        <Fragment key={collaborator?.id}>
            <ActiveAvatarHoverDropdown
                collaborator={collaborator}
                resourceId={resourceId}
                tooltipPlacement={tooltipPlacement}
                type={type}
                activity={activity as ActivityPayload}
            >
                <Avatar
                    src={user?.avatar}
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        backgroundColor: token.colorBgBase,
                        // Dynamic
                        opacity: avatarOpacity,
                        transition: `${presenceStyle.transition}, transform 0.25s ease`,
                        transform: `scale(${scale})`,
                    }}
                    size={size ?? 'default'}
                />
            </ActiveAvatarHoverDropdown>
        </Fragment>
    );
};

export default ActiveAvatarItem;
